import { z } from 'zod';

// ============================================================
// Profile Update Schema
// ============================================================

export const updateProfileSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name is too long')
    .optional(),
  homeAddress: z
    .string()
    .max(500, 'Address is too long')
    .optional()
    .or(z.literal('')),
  department: z
    .string()
    .max(100, 'Department name is too long')
    .optional()
    .or(z.literal('')),
});

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

// ============================================================
// Admin User Management Schemas
// ============================================================

export const roleEnum = z.enum(['STAFF', 'ADMIN', 'FINANCE', 'FINANCE_LEAD', 'HR', 'IT_ADMIN']);
export type UserRole = z.infer<typeof roleEnum>;

export const createUserSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  email: z.string().email('Valid email required'),
  role: roleEnum.default('STAFF'),
  department: z.string().max(100).optional(),
});

export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserRoleSchema = z.object({
  role: roleEnum.optional(),
  name: z.string().min(2).max(100).optional(),
  department: z.string().max(100).optional(),
  homeAddress: z.string().max(500).optional(),
});

export type UpdateUserRoleInput = z.infer<typeof updateUserRoleSchema>;

// ============================================================
// User Location Schemas (Story A.1 / Issue #7)
// ============================================================

export const userLocationTypeEnum = z.enum(['HOME', 'OFFICE', 'OTHER']);
export type UserLocationType = z.infer<typeof userLocationTypeEnum>;

export const createUserLocationSchema = z.object({
  label: z.string().min(1, 'Label is required').max(100, 'Label is too long'),
  address: z.string().min(1, 'Address is required').max(500, 'Address is too long'),
  locationType: userLocationTypeEnum.default('OTHER'),
  isDefault: z.boolean().default(false),
});

export type CreateUserLocationInput = z.infer<typeof createUserLocationSchema>;

export const updateUserLocationSchema = z.object({
  label: z.string().min(1, 'Label is required').max(100, 'Label is too long').optional(),
  address: z.string().min(1, 'Address is required').max(500, 'Address is too long').optional(),
  locationType: userLocationTypeEnum.optional(),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

export type UpdateUserLocationInput = z.infer<typeof updateUserLocationSchema>;

export const userLocationResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  label: z.string(),
  address: z.string(),
  locationType: z.string(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.date().or(z.string()),
  updatedAt: z.date().or(z.string()),
});

export type UserLocationResponse = z.infer<typeof userLocationResponseSchema>;

